import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  InfoCircleFilled,
  ExclamationCircleFilled,
  LoadingOutlined,
  CloseOutlined,
} from '@ant-design/icons';

const MessageContext = createContext();

// ── Per-type visual tokens ─────────────────────────────────────────────────
const TYPE_MAP = {
  success: {
    icon: <CheckCircleFilled style={{ fontSize: 16 }} />,
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    border: '#86efac',
    color: '#166534',
    iconColor: '#22c55e',
  },
  error: {
    icon: <CloseCircleFilled style={{ fontSize: 16 }} />,
    background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
    border: '#fca5a5',
    color: '#991b1b',
    iconColor: '#ef4444',
  },
  warning: {
    icon: <ExclamationCircleFilled style={{ fontSize: 16 }} />,
    background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
    border: '#fcd34d',
    color: '#92400e',
    iconColor: '#f59e0b',
  },
  info: {
    icon: <InfoCircleFilled style={{ fontSize: 16 }} />,
    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
    border: '#93c5fd',
    color: '#1e40af',
    iconColor: '#3b82f6',
  },
  loading: {
    icon: <LoadingOutlined style={{ fontSize: 16 }} spin />,
    background: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)',
    border: '#5eead4',
    color: '#0e9191',
    iconColor: '#0e9191',
  },
};

// Callers (e.g. app.save's .catch) sometimes pass raw error objects/arrays;
// rendering those as a React child crashes the whole tree (React error #31).
const toRenderable = (content) => {
  if (content == null || typeof content === 'string' || typeof content === 'number') return content;
  if (React.isValidElement(content)) return content;
  if (Array.isArray(content)) return content.map(toRenderable).filter(Boolean).join('; ');
  if (typeof content === 'object') {
    if (typeof content.message === 'string' && content.message) return content.message;
    if (content.error != null) return toRenderable(content.error);
    try { return JSON.stringify(content); } catch (e) { return String(content); }
  }
  return String(content);
};

export const MessageProvider = ({ children }) => {
  const [currentMessage, setCurrentMessage] = useState(null);
  const timerRef = useRef(null);

  const clearMessage = useCallback(() => {
    setCurrentMessage(null);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * showMessage(content, type, duration)
   * type: 'success' | 'error' | 'warning' | 'info' | 'loading'
   * duration: seconds before auto-close (0 = stay open)
   */
  const showMessage = useCallback((content, type = 'info', duration = 4) => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const tokens = TYPE_MAP[type] || TYPE_MAP.info;
    setCurrentMessage({ content: toRenderable(content), type, tokens, timestamp: Date.now() });

    // Auto-dismiss
    if (duration > 0) {
      timerRef.current = setTimeout(() => {
        setCurrentMessage(null);
        timerRef.current = null;
      }, duration * 1000);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <MessageContext.Provider value={{ showMessage, currentMessage, clearMessage }}>
      {children}
    </MessageContext.Provider>
  );
};

/**
 * Hook for showing messages — returns showMessage function.
 * For backward compatibility, calling useMessage() returns showMessage directly.
 */
export const useMessage = () => {
  const ctx = useContext(MessageContext);
  // Return the showMessage function directly for backward compatibility
  return ctx?.showMessage || ctx;
};

/**
 * Hook for reading current message state (used by the header banner).
 */
export const useMessageState = () => {
  const ctx = useContext(MessageContext);
  return { currentMessage: ctx?.currentMessage, clearMessage: ctx?.clearMessage };
};

/**
 * The banner component rendered inside the top header.
 */
export const HeaderMessageBanner = () => {
  const { currentMessage, clearMessage } = useMessageState();

  if (!currentMessage) return null;

  const { content, tokens } = currentMessage;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: tokens.background,
        border: `1px solid ${tokens.border}`,
        borderRadius: 8,
        padding: '6px 12px',
        maxWidth: 500,
        minWidth: 180,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        animation: 'bannerSlideIn 0.25s ease-out',
        flex: '0 1 auto',
        overflow: 'hidden',
      }}
    >
      <span style={{ color: tokens.iconColor, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        {tokens.icon}
      </span>
      <span
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: tokens.color,
          lineHeight: 1.4,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {content}
      </span>
      <CloseOutlined
        onClick={clearMessage}
        style={{
          fontSize: 11,
          color: tokens.color,
          opacity: 0.5,
          cursor: 'pointer',
          flexShrink: 0,
          marginLeft: 4,
        }}
      />
      <style>{`
        @keyframes bannerSlideIn {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};
